export type ProjectStage =
  | 'upload'
  | 'context-review'  // legacy — maps to upload
  | 'intake'
  | 'deep-probe'      // legacy — merged into intake session
  | 'confirmation'
  | 'outputs'
  | 'dashboard'

export interface ProjectPipelineCounts {
  new: number        // identified + prepared (not yet outreached)
  outreach: number   // in outreach
  shortlist: number  // shortlisted
  interview: number  // in interview
}

export interface ProjectSummary {
  id: string
  title: string
  stage: ProjectStage
  owner: string
  lastUpdated: string
  openTasks: number
  pipelineCounts?: ProjectPipelineCounts
}

export interface ProjectListResponse {
  projects: ProjectSummary[]
}

export interface CreateProjectRequest {
  title: string
  company: string
  regionFocus?: string
  owner: string
  collaborators?: string[]
}

export interface ProjectDetailResponse extends ProjectSummary {
  regionFocus?: string
  collaborators: string[]
}

export type DocumentStatus = 'processing' | 'processed' | 'ready'

export interface DocumentRecord {
  id: string
  name: string
  kind: 'jd' | 'org-chart' | 'notes' | 'other'
  status: DocumentStatus
  uploadedAt: string
  extractedText?: string
  wordCount?: number
}

export interface DocumentUploadRequest {
  files: Array<{ name: string; kind: DocumentRecord['kind']; size: number; contentType: string }>
}

export interface DocumentUploadResponse {
  documents: DocumentRecord[]
}

export interface ContextReviewResponse {
  summary: string[]
  missingInfo: string[]
  suggestedQuestions: string[]
}

export type IntakeSender = 'erica' | 'recruiter'

export interface IntakeMessage {
  id: string
  sender: IntakeSender
  text: string
  createdAt: string
}

export interface RoleHighlights {
  mustHaves: string[]
  niceToHaves: string[]
  leadershipSignals: string[]
}

export interface IntakeThreadResponse {
  messages: IntakeMessage[]
  highlights: RoleHighlights
}

export interface IntakeMessageRequest {
  text: string
}

export interface DeepProbeQuestion {
  id: string
  category: string
  prompt: string
}

export interface DeepProbeResponse {
  questions: DeepProbeQuestion[]
}

// ─── Candidate source ─────────────────────────────────────────────────────────

export type CandidateSource = 'applied' | 'sourced' | 'referred' | 'uploaded'

// Score band — routing decision based on fit score
// <40 = low_fit, 40–64 = review, 65–79 = qualify (Good Fit), >=80 = strong_fit
export type CandidateScoreBand = 'low_fit' | 'review' | 'qualify' | 'strong_fit'

export function scoreToBand(score: number): CandidateScoreBand {
  if (score >= 80) return 'strong_fit'
  if (score >= 65) return 'qualify'
  if (score >= 40) return 'review'
  return 'low_fit'
}

export const SCORE_BAND_CONFIG: Record<CandidateScoreBand, {
  label: string
  color: string
  bg: string
  border: string
  description: string
}> = {
  strong_fit: { label: 'Strong Fit', color: 'text-green-400',  bg: 'bg-green-500/10',  border: 'border-green-500/30',  description: 'Excellent fit — prioritise for outreach' },
  qualify:    { label: 'Good Fit',   color: 'text-[#FD802E]',  bg: 'bg-[#FD802E]/10',  border: 'border-[#FD802E]/30',  description: 'Good fit — advance to pre-screen' },
  review:     { label: 'Review',     color: 'text-yellow-400',  bg: 'bg-yellow-500/10',  border: 'border-yellow-500/30',  description: 'Potential fit — recruiter to assess' },
  low_fit:    { label: 'Low Fit',    color: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/30',     description: 'Below threshold — consider rejecting' },
}

export const SOURCE_CONFIG: Record<CandidateSource, {
  label: string
  color: string
  bg: string
}> = {
  applied:  { label: 'Applied',  color: 'text-blue-400',   bg: 'bg-blue-500/10' },
  sourced:  { label: 'Sourced',  color: 'text-[#FD802E]',  bg: 'bg-[#FD802E]/10' },
  referred: { label: 'Referred', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  uploaded: { label: 'Uploaded', color: 'text-gray-400',   bg: 'bg-gray-500/10' },
}

export interface CandidateScreeningResult {
  projectId: string
  candidateId: string
  candidateName: string
  candidateTitle: string
  fitScore: number
  matchedHighlights: string[]
  gaps: string[]
  recommendation: 'strong-hire' | 'hire' | 'borderline' | 'no-hire'
  rationale: string
  scoreBreakdown: {
    baseScore:       number
    matchBonus:      number
    gapPenalty:      number
    personFitScore?: number | null   // 0–60 — from new dual-dimension prompt
    companyFitScore?: number | null  // 0–25 — from new dual-dimension prompt
  }
  source?: CandidateSource
  scoreBand?: CandidateScoreBand
  linkedinUrl?: string
  cvUrl?: string
  pipelineStage?: string  // 'identified' | 'outreach' | 'shortlisted' | 'interview' | 'offer'
  summary?: string        // career/background summary
  locationText?: string   // "City, Country" — direct from Apollo, no regex parsing needed
  created_at?: string     // ISO timestamp — used to detect "New" candidates (within 3 days)
  email?: string          // contact email — from CV upload or Apollo; may be absent
  phone?: string          // contact phone — from CV upload; may be absent
}

export interface ConfirmationSummaryResponse {
  projectId: string
  highlights: RoleHighlights
  probeThemes: string[]
  probeSynthesisNotes: string[]
  dealbreakersContext: string[]
  whyRoleContext: string[]
  intakeMessageCount: number
  probeAnswerCount: number
  readyForOutputs: boolean
}

export interface DeepProbeAnswer {
  questionId: string
  answer: string
}

export interface DeepProbeAnswerRequest {
  answers: DeepProbeAnswer[]
}

export interface DeepProjectContext {
  projectId: string
  synthesisNotes: string[]
  keyThemes: string[]
  readyForOutputs: boolean
}

export interface DeepProbeAnswerResponse {
  projectContext: DeepProjectContext
}

export interface OutputDrafts {
  jd: string
  brief: string
  interviewGuide: string
  scorecard: string
  summary: string
  outreach: string
}

export interface OutputGenerationResponse {
  projectId: string
  drafts: OutputDrafts
  version: number
}

export interface CandidateProfile {
  id: string
  name: string
  title: string
  fitScore: number
  highlights: string[]
  risks: string[]
  summary: string
}

export interface CandidateReviewResponse {
  projectId: string
  candidate: CandidateProfile
}

export interface CandidateListResponse {
  projectId: string
  candidates: CandidateScreeningResult[]
}

export interface CandidateCVUploadResponse {
  ok: boolean
  candidate: {
    id: string
    full_name: string
    title: string
    fitScore: number
    scoreBand: CandidateScoreBand
    recommendation: string
  }
}

export interface CandidateCsvUploadResponse {
  projectId: string
  uploaded: number
  ranked: CandidateScreeningResult[]
}

export interface CandidateDeepDiveConversationTurn {
  id: string
  speaker: 'erica' | 'candidate'
  text: string
  createdAt: string
}

export interface CandidateDeepDiveSummary {
  candidateId: string
  targetRoles: string[]
  locations: string[]
  compensation: string
  mustHaves: string[]
  niceToHaves: string[]
}

export interface CandidateDeepDiveResponse {
  conversation: CandidateDeepDiveConversationTurn[]
  summary: CandidateDeepDiveSummary
}

export interface DashboardMetric {
  key: string
  label: string
  value: string
  trend?: string
}

export interface DashboardSummaryResponse {
  projectId: string
  metrics: DashboardMetric[]
}

// ─── Headhunt (Track B) types ─────────────────────────────────────────────────

export type CompanySizeBand = 'startup' | 'scaleup' | 'mid-market' | 'enterprise'

export interface TargetCompany {
  name:         string
  apolloOrgId?: string   // populated after Task 2 org resolution runs
  domain?:      string
  reason:       string
  tier:         'primary' | 'secondary' | 'lookalike'
}

export interface TargetCompanyPack {
  clientSector:           string
  clientStage:            string
  clientBusinessModel:    string
  targetCompanies:        TargetCompany[]
  excludedCompanies:      string[]
  preferredSectors:       string[]
  preferredSizes:         CompanySizeBand[]
  preferredStages:        string[]
  preferredBusinessModel: string[]
  reasoning:              string
  apolloResolved:         boolean
  generatedAt:            string
}

export interface HeadhuntSearchBrief {
  projectId:          string
  roleTitle:          string
  personTitles:       string[]
  personSeniorities:  string[]
  personLocations:    string[]
  mustHaves:          string[]
  niceToHaves:        string[]
  keywords:           string[]
  intakeSummary:      string
  targetCompanyPack?: TargetCompanyPack   // MVP2 — present when context is sufficient
  generatedAt:        string
}

export interface HeadhuntBriefResponse {
  projectId: string
  brief:     HeadhuntSearchBrief
}

export interface HeadhuntLaunchRequest {
  overrides?: {
    personTitles?:      string[]
    personSeniorities?: string[]
    personLocations?:   string[]
    keywords?:          string[]
  }
}

export interface HeadhuntLaunchResponse {
  projectId:          string
  brief:              HeadhuntSearchBrief
  candidatesFound:    number
  candidatesAdded:    number
  candidatesSkipped?: number
  apolloPage?:        number       // which page of Apollo results this sprint used
  resultVariety?:     'fresh' | 'all_duplicate'  // whether any new candidates were added
  status:             'complete' | 'failed' | 'no_apollo_key' | 'no_candidates'
  error?:             string
}

export interface HeadhuntStatusResponse {
  projectId:       string
  hasRun:          boolean
  candidatesAdded: number
  lastRunAt:       string | null     // ISO timestamp, null if never run
}

export interface RerankSourcedResponse {
  projectId: string
  reranked:  number
  skipped:   number
}

export const engineEndpoints = {
  projects: '/api/engine/projects',
  project: (projectId: string) => `/api/engine/projects/${projectId}`,
  documents: (projectId: string) => `/api/engine/projects/${projectId}/documents`,
  documentsUpload: (projectId: string) => `/api/engine/projects/${projectId}/documents/upload`,
  contextReview: (projectId: string) => `/api/engine/projects/${projectId}/context`,
  intake: (projectId: string) => `/api/engine/projects/${projectId}/intake`,
  intakeMessages: (projectId: string) => `/api/engine/projects/${projectId}/intake/messages`,
  intakeHighlights: (projectId: string) => `/api/engine/projects/${projectId}/intake/highlights`,
  deepProbe: (projectId: string) => `/api/engine/projects/${projectId}/deep-probe`,
  deepProbeAnswers: (projectId: string) => `/api/engine/projects/${projectId}/deep-probe/answers`,
  confirmation: (projectId: string) => `/api/engine/projects/${projectId}/confirmation`,
  projectStage: (projectId: string) => `/api/engine/projects/${projectId}/stage`,
  candidates: (projectId: string) => `/api/engine/projects/${projectId}/candidates`,
  candidatesCsvUpload: (projectId: string) => `/api/engine/projects/${projectId}/candidates/upload-csv`,
  candidatesCvUpload: (projectId: string) => `/api/engine/projects/${projectId}/candidates/upload-cv`,
  candidateScreening: (projectId: string, candidateId: string) =>
    `/api/engine/projects/${projectId}/candidates/${candidateId}/screening`,
  outputs: (projectId: string) => `/api/engine/projects/${projectId}/outputs`,
  outputsDraft: (projectId: string) => `/api/engine/projects/${projectId}/outputs`,
  outputUpdate: (projectId: string, outputType: string) => `/api/engine/projects/${projectId}/outputs/${outputType}`,
  scorecardResponses: (projectId: string) => `/api/engine/projects/${projectId}/outputs/scorecard/responses`,
  candidate: (projectId: string, candidateId: string) =>
    `/api/engine/projects/${projectId}/candidates/${candidateId}`,
  candidateDeepDive: (candidateId: string) => `/api/engine/candidates/${candidateId}/deep-dive`,
  dashboard: (projectId: string) => `/api/engine/projects/${projectId}/dashboard`,
  // Search sprint (uploaded pool)
  searchLaunch: (projectId: string) => `/api/engine/projects/${projectId}/search/launch`,
  searchStatus: (projectId: string) => `/api/engine/projects/${projectId}/search/status`,
  // Headhunt sprint (Track B — Apollo live sourcing)
  candidateStage:      (projectId: string, candidateId: string) => `/api/engine/projects/${projectId}/candidates/${candidateId}/stage`,
  candidateScoreBand:  (projectId: string, candidateId: string) => `/api/engine/projects/${projectId}/candidates/${candidateId}/score-band`,
  preScreenSendLink:   (projectId: string, candidateId: string) => `/api/engine/projects/${projectId}/candidates/${candidateId}/pre-screen/send-link`,
  preScreenPublicStart:  (token: string) => `/api/prescreen/${token}/start`,
  preScreenPublicSubmit: (token: string) => `/api/prescreen/${token}/submit`,
  candidatesBulkStage: (projectId: string) => `/api/engine/projects/${projectId}/candidates/bulk/stage`,
  candidatesBulkDelete:(projectId: string) => `/api/engine/projects/${projectId}/candidates/bulk`,
  headhuntBrief: (projectId: string) => `/api/engine/projects/${projectId}/headhunt/brief`,
  headhuntLaunch: (projectId: string) => `/api/engine/projects/${projectId}/headhunt/launch`,
  headhuntStatus: (projectId: string) => `/api/engine/projects/${projectId}/headhunt/status`,
  headhuntClearSourced: (projectId: string) => `/api/engine/projects/${projectId}/headhunt/sourced`,
  headhuntRerank: (projectId: string) => `/api/engine/projects/${projectId}/headhunt/rerank`,
  // Outreach
  outreachList: (projectId: string) => `/api/engine/projects/${projectId}/outreach`,
  outreachGenerate: (projectId: string, candidateId: string) =>
    `/api/engine/projects/${projectId}/candidates/${candidateId}/outreach`,
  outreachSend: (projectId: string, candidateId: string) =>
    `/api/engine/projects/${projectId}/candidates/${candidateId}/outreach/send`,
  outreachDraft: (projectId: string, candidateId: string) =>
    `/api/engine/projects/${projectId}/candidates/${candidateId}/outreach/draft`,
  // Pre-screen
  preScreenStart: (projectId: string, candidateId: string) =>
    `/api/engine/projects/${projectId}/candidates/${candidateId}/pre-screen/start`,
  preScreenComplete: (projectId: string, candidateId: string) =>
    `/api/engine/projects/${projectId}/candidates/${candidateId}/pre-screen/complete`,
  preScreenRecord: (projectId: string, candidateId: string) =>
    `/api/engine/projects/${projectId}/candidates/${candidateId}/pre-screen`,
  // Candidate event timeline
  candidateEvents: (projectId: string, candidateId: string) =>
    `/api/engine/projects/${projectId}/candidates/${candidateId}/events`,
  // Cost tracking
  costs: '/api/engine/costs',
  // Analytics
  analytics: '/api/engine/analytics',
  // Shortlist finalise
  shortlistFinalise: (projectId: string) => `/api/engine/projects/${projectId}/shortlist/finalise`,
  // Follow-up
  outreachFollowUp: (projectId: string) => `/api/engine/projects/${projectId}/outreach/follow-up`,
  // Tasks
  tasks: '/api/engine/tasks',
  task: (taskId: string) => `/api/engine/tasks/${taskId}`,
} as const

export interface EngineApi {
  listProjects(): Promise<ProjectListResponse>
  createProject(payload: CreateProjectRequest): Promise<ProjectDetailResponse>
  fetchProject(projectId: string): Promise<ProjectDetailResponse>
  uploadDocuments(projectId: string, payload: DocumentUploadRequest): Promise<DocumentUploadResponse>
  uploadDocumentFile(projectId: string, file: File, kind: DocumentRecord['kind']): Promise<DocumentUploadResponse>
  listDocuments(projectId: string): Promise<DocumentUploadResponse>
  getContextReview(projectId: string): Promise<ContextReviewResponse>
  getIntakeThread(projectId: string): Promise<IntakeThreadResponse>
  postIntakeMessage(projectId: string, payload: IntakeMessageRequest): Promise<IntakeThreadResponse>
  patchIntakeHighlights(projectId: string, highlights: RoleHighlights): Promise<IntakeThreadResponse>
  getDeepProbeQuestions(projectId: string): Promise<DeepProbeResponse>
  submitDeepProbeAnswers(projectId: string, payload: DeepProbeAnswerRequest): Promise<DeepProbeAnswerResponse>
  getConfirmationSummary(projectId: string): Promise<ConfirmationSummaryResponse>
  advanceProjectStage(projectId: string, stage: ProjectStage): Promise<ProjectDetailResponse>
  listCandidates(projectId: string): Promise<CandidateListResponse>
  uploadCandidateCsv(projectId: string, file: File): Promise<CandidateCsvUploadResponse>
  uploadCandidateCV(projectId: string, file: File): Promise<CandidateCVUploadResponse>
  getCandidateScreening(projectId: string, candidateId: string): Promise<CandidateScreeningResult>
  generateOutputs(projectId: string): Promise<OutputGenerationResponse>
  getOutputs(projectId: string): Promise<OutputGenerationResponse>
  getCandidateReview(projectId: string, candidateId: string): Promise<CandidateReviewResponse>
  getCandidateDeepDive(candidateId: string): Promise<CandidateDeepDiveResponse>
  getDashboardSummary(projectId: string): Promise<DashboardSummaryResponse>
  updateOutput(projectId: string, outputType: string, content: string): Promise<{ ok: boolean }>
  getScorecardResponses(projectId: string): Promise<ScorecardResponsesApiResult>
  saveScorecardResponses(projectId: string, responses: ScorecardResponses): Promise<{ ok: boolean }>
  // Search sprint
  launchSearch(projectId: string): Promise<LaunchSearchResponse>
  getSearchStatus(projectId: string): Promise<SearchSprintState>
  // Outreach
  listOutreach(projectId: string): Promise<OutreachListResponse>
  generateOutreach(projectId: string, candidateId: string): Promise<GenerateOutreachResponse>
  sendOutreach(projectId: string, candidateId: string): Promise<{ ok: boolean }>
  updateOutreachDraft(projectId: string, candidateId: string, emailSubject: string, emailBody: string): Promise<{ ok: boolean }>
  // Pre-screen
  getPreScreen(projectId: string, candidateId: string): Promise<PreScreenRecord>
  submitPreScreen(projectId: string, candidateId: string, responses: PreScreenQA[]): Promise<SubmitPreScreenResponse>
  // Candidate event timeline
  getCandidateEvents(projectId: string, candidateId: string): Promise<CandidateEventsResponse>
  // Cost tracking
  getCosts(): Promise<CostsResponse>
  // Pre-screen link
  sendPreScreenLink(projectId: string, candidateId: string): Promise<{ ok: boolean }>
  // Shortlist finalise
  finaliseShortlist(projectId: string): Promise<{ ok: boolean; candidateCount: number }>
}

// ─── Search Sprint ──────────────────────────────────────────────────────────

export type SearchSprintStatus =
  | 'not_started'
  | 'in_progress'
  | 'complete'
  | 'failed'

export interface SearchSprintState {
  projectId: string
  status: SearchSprintStatus
  startedAt: string | null
  completedAt: string | null
  candidatesFound: number
  candidatesRanked: number
  topN: number // always 50 for MVP2
  error?: string
}

export interface LaunchSearchResponse {
  projectId: string
  sprint: SearchSprintState
}

// ─── Outreach ───────────────────────────────────────────────────────────────

export type OutreachStatus =
  | 'not_started'
  | 'prepared'
  | 'sent'
  | 'email_opened'
  | 'link_clicked'
  | 'pre_screen_started'
  | 'pre_screen_completed'
  | 'abandoned'
  | 'no_response'
  // Legacy — retained for backwards compatibility with older records
  | 'responded'
  | 'booked'

export interface OutreachDraft {
  candidateId: string
  emailSubject: string
  emailBody: string
  linkedinMessage: string
  calendarLink: string
  generatedAt: string
}

export interface OutreachStateRecord {
  candidateId: string
  candidateName: string
  status: OutreachStatus
  draft?: OutreachDraft
  sentAt?: string
  respondedAt?: string
  bookedAt?: string
}

export interface GenerateOutreachResponse {
  projectId: string
  candidateId: string
  draft: OutreachDraft
}

export interface OutreachListResponse {
  projectId: string
  outreach: OutreachStateRecord[]
}

// ─── Pre-screen ──────────────────────────────────────────────────────────────

export type PreScreenStatus =
  | 'not_started'
  | 'scheduled'
  | 'complete'
  | 'declined'

export interface PreScreenQA {
  question: string
  answer: string
}

export interface PreScreenRecord {
  candidateId: string
  projectId: string
  status: PreScreenStatus
  completedAt: string | null
  questionsAsked: string[]
  responses: PreScreenQA[]
  postScreenScore: number | null
  postScreenRationale: string | null
  mustHaveCoverage: { criterion: string; confirmed: boolean; notes: string }[]
  ericaRecommendation: 'progress' | 'hold' | 'decline' | null
}

export interface SubmitPreScreenResponse {
  projectId: string
  candidateId: string
  preScreen: PreScreenRecord
  updatedScore: number
}

// ─── Scorecard persistence ────────────────────────────────────────────────────

export interface ScorecardCriterion {
  id: string
  title: string
  description: string
  score: number | null
  notes: string
}

export interface ScorecardResponses {
  candidateName: string
  currentRole: string
  interviewDate: string
  interviewer: string
  criteria: ScorecardCriterion[]
  recommendation: 'strong-yes' | 'yes' | 'maybe' | 'no' | null
  savedAt: string
}

export interface ScorecardResponsesApiResult {
  responses: ScorecardResponses | null
}

// ─── Candidate event timeline ────────────────────────────────────────────────

// Lightweight event item shape — used for timeline display
export interface CandidateEventItem {
  event_type: string
  created_at: string
  metadata?: Record<string, unknown>
}

export type CandidateEventType =
  | 'email_sent'
  | 'email_opened'
  | 'link_clicked'
  | 'pre_screen_started'
  | 'pre_screen_completed'
  | 'abandoned'
  | 'no_response'

export interface CandidateEvent {
  event_type: CandidateEventType
  created_at: string
  metadata: Record<string, unknown>
}

export interface CandidateEventsResponse {
  events: CandidateEvent[]
}

// ─── Inbound application ──────────────────────────────────────────────────────

// ─── Pre-screen token payload (public — returned by /api/prescreen/:token/start) ─
export interface PreScreenTokenPayload {
  candidateId:             string
  projectId:               string
  candidateName:           string
  questions:               string[]
  hasCompletedCareerCoach?: boolean
  roleTitle?:              string
  projectTitle?:           string
}

export interface InboundApplicationRequest {
  fullName: string
  email: string
  currentTitle: string
  currentCompany: string
  linkedinUrl?: string
  coverNote?: string
}

export interface InboundApplicationResponse {
  ok: boolean
  candidateId: string
  scoreBand: CandidateScoreBand
  message: string  // what the applicant sees
}

// ─── Free tier ────────────────────────────────────────────────────────────────

// ─── Cost tracking ───────────────────────────────────────────────────────────

export interface CostByModel {
  model: string
  totalCostUsd: number
  totalTokens: number
  calls: number
}

export interface CostByUsageType {
  usageType: string
  totalCostUsd: number
  totalTokens: number
  calls: number
}

export interface CostByProject {
  projectId: string
  projectTitle: string
  totalCostUsd: number
  totalTokens: number
  calls: number
}

export interface CostDailyPoint {
  date: string
  totalCostUsd: number
}

export interface CostsResponse {
  totalCostUsd: number
  totalTokens: number
  byModel: CostByModel[]
  byUsageType: CostByUsageType[]
  byProject: CostByProject[]
  last30Days: CostDailyPoint[]
}

export interface TierStatus {
  projectCount: number
  maxProjects: number
  sprintCount: number
  maxSprints: number
  outreachCount: number
  maxOutreaches: number
  canCreateProject: boolean
  canLaunchSprint: boolean
  canSendOutreach: boolean
}
